import React, {useMemo} from "react";
import {ListItemText} from "@mui/material";
import {Box, Checkbox, Chip, Colors, Text} from "@bounce/shared/ui";
import {MenuItem, Select} from "../../../ui/components";
import {ReachSegment} from "../../../apis/requests/templates/types";
import {groupSegmentTags, selectableSegments} from "../../templates/segmentTagSelection";
import {INPUT_SX} from "./FormCard";

const PLACEHOLDER = "Select segments";
const EXCLUSIVE_LABEL = "Exclusive";
const SELECTION_CONTROL_SX = {margin: "2px 12px 2px 6px", padding: 0};

type Props = {
  segments: ReachSegment[];
  value: number[];
  onChange: (segmentIds: number[]) => void;
};

const PromoSegmentsSelect = ({segments, value, onChange}: Props) => {
  const options = useMemo(() => selectableSegments(groupSegmentTags(segments, false)), [segments]);
  const segmentById = useMemo(() => new Map(options.map((segment) => [segment.id, segment])), [options]);

  return (
    <Select
      multiple
      fullWidth
      size="small"
      displayEmpty
      value={value}
      onChange={(event) => onChange(event.target.value as number[])}
      sx={INPUT_SX}
      renderValue={(selected) => {
        const ids = (selected as number[]).filter((id) => segmentById.has(id));
        if (ids.length === 0)
          return (
            <Text size={14} sx={{color: Colors.grey._400}}>
              {PLACEHOLDER}
            </Text>
          );
        return (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
            {ids.map((id) => {
              const segment = segmentById.get(id)!;
              return segment.type === "EXCLUSIVE" ? (
                <Chip key={id} size_="small" variant_="info" label={`${EXCLUSIVE_LABEL} · ${segment.name}`} />
              ) : (
                <Chip key={id} size_="small" variant_="blue" label={segment.name} />
              );
            })}
          </Box>
        );
      }}
    >
      {options.map((segment) => (
        <MenuItem key={segment.id} value={segment.id}>
          <Checkbox checked={value.includes(segment.id)} sx={SELECTION_CONTROL_SX} />
          <ListItemText primary={segment.name} secondary={segment.type === "EXCLUSIVE" ? EXCLUSIVE_LABEL : undefined} />
        </MenuItem>
      ))}
    </Select>
  );
};

export default PromoSegmentsSelect;
